// Copyright 2026, University of Maryland, All Rights Reserved
/**
 * Register contract-review renderer components.
 */
import { registerRendererComponent } from '@/app-shell/renderer-components'
import { DiffViewerRenderer } from './DiffViewerRenderer'
import { ContractAnalysisRenderer } from './ContractAnalysisRenderer'
import { ClauseAnalysisRenderer } from './ClauseAnalysisRenderer'
import { NegotiationRenderer } from './NegotiationRenderer'
import { SectionReportRenderer } from './SectionReportRenderer'

registerRendererComponent({
  id: 'contract-analysis',
  label: 'Contract Analysis',
  group: 'Contract Review',
  versionMode: 'single',
  slots: [
    {
      key: 'summary',
      type: 'dataset',
      label: 'Summary',
      description: 'Headline and summary text shown above findings.',
    },
    {
      key: 'findings',
      type: 'dataset',
      label: 'Findings',
      description: 'Risk-scored findings produced by analysis.',
    },
  ],
  redline: {
    slotKey: 'findings',
    label: 'Download Redline',
    filenamePrefix: 'UMD Redlines',
  },
  Component: ContractAnalysisRenderer,
})

registerRendererComponent({
  id: 'clause-analysis',
  label: 'Clause Analysis',
  group: 'Contract Review',
  versionMode: 'single',
  slots: [
    {
      key: 'clauses',
      type: 'dataset',
      label: 'Clauses',
      description: 'Identified clauses (FAR/DFARS/regulatory).',
    },
  ],
  redline: {
    slotKey: 'clauses',
    label: 'Download Redline',
    filenamePrefix: 'UMD Redlines',
  },
  Component: ClauseAnalysisRenderer,
})

registerRendererComponent({
  id: 'negotiation',
  label: 'Negotiation',
  group: 'Contract Review',
  versionMode: 'single',
  slots: [
    {
      key: 'negotiation',
      type: 'dataset',
      required: true,
      label: 'Negotiation Findings',
      description: 'Tracked-change recommendations grouped by KB item. The dataset must have a knowledge base bound to it.',
    },
    {
      key: 'troublesomeClauses',
      type: 'dataset',
      required: false,
      label: 'Troublesome Clauses',
      description: 'Clauses flagged for legal/procurement reasons. The dataset must have a knowledge base bound to it.',
    },
    {
      key: 'contractDuplicates',
      type: 'dataset',
      required: false,
      label: 'Contract Duplicates',
      description: 'Duplicate/conflicting contract language findings shown in the Duplicates tab.',
    },
    {
      key: 'negotiationEmail',
      type: 'dataset',
      required: false,
      label: 'Negotiation Email',
      description: 'AI-drafted negotiation email (markdown body).',
    },
  ],
  Component: NegotiationRenderer,
})

registerRendererComponent({
  id: 'section-report',
  label: 'Section Report',
  group: 'Contract Review',
  versionMode: 'single',
  slots: [
    {
      key: 'sections',
      type: 'dataset',
      required: true,
      label: 'Sections',
      description: 'Markdown sections produced by KB-driven report generation.',
    },
  ],
  Component: SectionReportRenderer,
})

// Single diff-viewer component. The legacy `dataset-diff-viewer` id is
// registered as an alias so existing renderer configs keep resolving until
// the m_normalize_renderer_slots migration renames them.
const DIFF_VIEWER_DEF = {
  label: 'Diff Viewer',
  group: 'Contract Review' as const,
  versionMode: 'compare' as const,
  slots: [
    {
      key: 'changes',
      type: 'dataset' as const,
      label: 'AI Analysis',
      description: 'Dataset of AI-detected changes shown in the AI Analysis tab. Optional — without it, the viewer shows only the text diff.',
    },
    {
      key: 'text',
      type: 'document_text' as const,
      required: true,
      multiVersion: true,
      label: 'Document Text',
      description: 'Full document text from each version, used to compute the diff.',
    },
  ],
  redline: {
    slotKey: 'changes',
    label: 'Download Diff',
    filenamePrefix: 'UMD Version Analysis',
  },
  Component: DiffViewerRenderer,
}

registerRendererComponent({ id: 'diff-viewer', ...DIFF_VIEWER_DEF })
registerRendererComponent({ id: 'dataset-diff-viewer', ...DIFF_VIEWER_DEF })
